/**
 * useAllPlayerCreatures Hook
 *
 * Loads all creatures for authenticated user from Firestore.
 * Includes user's own creatures + shared pool.
 * Used when no specific creature IDs are provided (e.g., in battle flow).
 */

import { useState, useEffect } from 'react';
import { getCreatureLibraryService } from '@/services/creatureLibraryService';
import { useAuth } from '@/contexts/AuthContext';
import type { SerializedCreatureDocument } from '@drawn-of-war/shared/types/creature-storage';

export interface AllPlayerCreaturesState {
  creatures: SerializedCreatureDocument[];
  loading: boolean;
  error: string | null;
}

/**
 * Wait for window.__getAuthToken to be available
 * This handles the race condition where user is set but the auth token effect hasn't run yet
 */
async function waitForAuthToken(maxWaitMs: number = 5000): Promise<boolean> {
  const startTime = Date.now();
  const checkInterval = 50; // Check every 50ms

  while (Date.now() - startTime < maxWaitMs) {
    if ((window as any).__getAuthToken) {
      return true;
    }
    await new Promise(resolve => setTimeout(resolve, checkInterval));
  }
  return false;
}

/**
 * Load all creatures for authenticated user (includes shared pool)
 *
 * @param enabled - Whether to fetch (default: true)
 * @param limit - Maximum number of creatures to load (default: 100)
 */
export function useAllPlayerCreatures(
  enabled: boolean = true,
  limit: number = 100
): AllPlayerCreaturesState {
  const { user, loading: authLoading } = useAuth();
  const [state, setState] = useState<AllPlayerCreaturesState>({
    creatures: [],
    loading: enabled,
    error: null
  });

  useEffect(() => {
    // Skip if not enabled
    if (!enabled) {
      setState({
        creatures: [],
        loading: false,
        error: null
      });
      return;
    }

    // Wait for auth to finish loading
    if (authLoading) {
      setState(prev => ({ ...prev, loading: true, error: null }));
      return;
    }

    // User must be authenticated
    if (!user) {
      setState({
        creatures: [],
        loading: false,
        error: 'Please log in to view your creatures'
      });
      return;
    }

    let cancelled = false;

    const fetchAllCreatures = async () => {
      setState(prev => ({ ...prev, loading: true, error: null }));

      try {
        // Wait for the auth token function to be available
        // This handles the race condition where user is set but __getAuthToken effect hasn't run
        const authTokenReady = await waitForAuthToken();
        if (!authTokenReady) {
          throw new Error('Auth token not available after waiting');
        }

        if (cancelled) return;

        const libraryService = getCreatureLibraryService();
        const response = await libraryService.listCreatures({
          limit,
          offset: 0
        });

        if (!cancelled) {
          setState({
            creatures: response.creatures,
            loading: false,
            error: null
          });
        }
      } catch (error) {
        console.error('[useAllPlayerCreatures] Error loading creatures:', error);

        if (!cancelled) {
          setState({
            creatures: [],
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to load creatures'
          });
        }
      }
    };

    fetchAllCreatures();

    return () => {
      cancelled = true;
    };
  }, [enabled, limit, authLoading, user]);

  return state;
}
